
import { Contact, Investor } from "@/types/investor";

// Find associated investor for a contact
export const getInvestorForFirm = (investors: Investor[], firmName: string): Investor | undefined => {
  return investors.find(investor => investor.firm?.toLowerCase() === firmName?.toLowerCase());
};

// Check if an investor has been processed (has a lastRelevanceUpdate timestamp)
export const isInvestorProcessed = (investor?: Investor): boolean => {
  return !!investor?.lastRelevanceUpdate;
};

// Filter and sort contacts based on search term, sort field, and direction
export const filterAndSortContacts = (
  contacts: Contact[],
  investors: Investor[],
  searchTerm: string,
  sortField: "name" | "firmName" | "relevance",
  sortDirection: "asc" | "desc"
): Contact[] => {
  return contacts
    .filter(contact => 
      contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      contact.firmName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.location?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortField === "name") {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return sortDirection === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      } 
      else if (sortField === "firmName") {
        const firmA = (a.firmName || "").toLowerCase();
        const firmB = (b.firmName || "").toLowerCase();
        return sortDirection === "asc" ? firmA.localeCompare(firmB) : firmB.localeCompare(firmA);
      } 
      else if (sortField === "relevance") {
        const investorA = getInvestorForFirm(investors, a.firmName || "");
        const investorB = getInvestorForFirm(investors, b.firmName || "");
        
        // Check if both investors are processed
        const aProcessed = isInvestorProcessed(investorA);
        const bProcessed = isInvestorProcessed(investorB);
        
        // If both are processed, sort by relevance score
        if (aProcessed && bProcessed) {
          const scoreA = investorA?.relevanceScore || 0;
          const scoreB = investorB?.relevanceScore || 0;
          return sortDirection === "asc" ? scoreA - scoreB : scoreB - scoreA;
        }
        
        // If only one is processed, prioritize processed investors
        if (aProcessed && !bProcessed) {
          return sortDirection === "asc" ? 1 : -1; // Processed last in asc, first in desc
        }
        
        if (!aProcessed && bProcessed) {
          return sortDirection === "asc" ? -1 : 1; // Unprocessed first in asc, last in desc
        }
        
        // If neither is processed, fall back to firm name
        const firmA = (a.firmName || "").toLowerCase();
        const firmB = (b.firmName || "").toLowerCase();
        return sortDirection === "asc" ? firmA.localeCompare(firmB) : firmB.localeCompare(firmA);
      }
      return 0;
    });
};
